/**
 * RetailRoom3D — voxel-style 3D viewer for a room plan, with no external library.
 *
 * Three renderers share one scene :
 *
 *   iso    inline SVG, 2:1 isometric projection, painter's algorithm. Crisp at
 *          any zoom, printable, and it survives a PDF export — this is the one
 *          shown to a customer. Objects can be dragged straight on the floor.
 *
 *   orbit  CSS 3D transforms, six faces per part, free rotation with the mouse.
 *
 *   eye    first-person perspective at a configurable height (1,75 m by
 *          default) : the room seen by someone standing in it. Drag to look,
 *          wheel or Z/S to walk, Q/D to sidestep.
 *
 * A scene comes from `ioi_retail_suite.api.scene` :
 *   { grid, room:{w,d,h}, boxes:[{id,parent,x,y,z,w,d,h,rot,pivot,color,material}] }
 * All lengths are centimetres. `rot` is [rx, ry, rz] in degrees, applied around
 * `pivot` — the centre of the object's base.
 *
 * The rigid-body maths below mirrors `engine/transform.py` line for line : the
 * PDF and the screen must draw the same room, and a test pins them together.
 */

/* global frappe */

const ISO_X = 0.866; // cos(30°)
const ISO_Y = 0.5; // sin(30°)
const NEAR_CM = 12; // near plane of the eye camera

// corner index = i + 2j + 4k, i along x, j along y, k along z
const CORNER_BITS = [];
for (let index = 0; index < 8; index++) {
	CORNER_BITS.push([index & 1, (index >> 1) & 1, (index >> 2) & 1]);
}

// [corner indices, outward normal] — wound CCW seen from outside, so a positive
// shoelace area after an affine projection means the face is visible
const FACES = [
	[[0, 2, 3, 1], [0, 0, -1]],
	[[4, 5, 7, 6], [0, 0, 1]],
	[[0, 1, 5, 4], [0, -1, 0]],
	[[2, 6, 7, 3], [0, 1, 0]],
	[[0, 4, 6, 2], [-1, 0, 0]],
	[[1, 3, 7, 5], [1, 0, 0]],
];

const RAD = Math.PI / 180;

function rotationMatrix(rx, ry, rz) {
	const ax = rx * RAD;
	const ay = ry * RAD;
	const az = rz * RAD;
	const cx = Math.cos(ax);
	const sx = Math.sin(ax);
	const cy = Math.cos(ay);
	const sy = Math.sin(ay);
	const cz = Math.cos(az);
	const sz = Math.sin(az);
	return [
		[cz * cy, cz * sy * sx - sz * cx, cz * sy * cx + sz * sx],
		[sz * cy, sz * sy * sx + cz * cx, sz * sy * cx - cz * sx],
		[-sy, cy * sx, cy * cx],
	];
}

function applyMatrix(m, x, y, z) {
	return [
		m[0][0] * x + m[0][1] * y + m[0][2] * z,
		m[1][0] * x + m[1][1] * y + m[1][2] * z,
		m[2][0] * x + m[2][1] * y + m[2][2] * z,
	];
}

function isIdentity(rot) {
	if (!rot) return true;
	return !(rot[0] % 360) && !(rot[1] % 360) && !(rot[2] % 360);
}

/** The eight corners of a part, in world centimetres, rotation applied. */
function boxCorners(box) {
	const x = box.x;
	const y = box.y;
	const z = box.z;
	const w = box.w;
	const d = box.d;
	const h = box.h;
	const base = CORNER_BITS.map((bit) => [x + bit[0] * w, y + bit[1] * d, z + bit[2] * h]);
	if (isIdentity(box.rot)) return base;
	const pivot = box.pivot || [x + w / 2, y + d / 2, z];
	const m = rotationMatrix(box.rot[0], box.rot[1], box.rot[2]);
	return base.map((c) => {
		const v = applyMatrix(m, c[0] - pivot[0], c[1] - pivot[1], c[2] - pivot[2]);
		return [pivot[0] + v[0], pivot[1] + v[1], pivot[2] + v[2]];
	});
}

function faceNormal(box, faceIndex) {
	const n = FACES[faceIndex][1];
	if (isIdentity(box.rot)) return n;
	return applyMatrix(rotationMatrix(box.rot[0], box.rot[1], box.rot[2]), n[0], n[1], n[2]);
}

/** Shade offset from a face normal — fitted to the tones the flat renderer used. */
function faceTone(n) {
	return 0.34 * n[2] - 0.02 * n[1] - 0.18 * n[0] - 0.16;
}

function shoelace(points) {
	let total = 0;
	for (let i = 0; i < points.length; i++) {
		const a = points[i];
		const b = points[(i + 1) % points.length];
		total += a[0] * b[1] - b[0] * a[1];
	}
	return total;
}

/** Distance from a point to a box footprint, 0 when the point is inside it. */
function clearanceTo(x, y, box) {
	const dx = Math.max(box.x - x, 0, x - (box.x + box.w));
	const dy = Math.max(box.y - y, 0, y - (box.y + box.d));
	return Math.sqrt(dx * dx + dy * dy);
}

function centroid3(points) {
	let x = 0;
	let y = 0;
	let z = 0;
	for (const p of points) {
		x += p[0];
		y += p[1];
		z += p[2];
	}
	return [x / points.length, y / points.length, z / points.length];
}

class RetailRoom3D {
	constructor(container, options = {}) {
		this.container = typeof container === "string"
			? document.querySelector(container)
			: container;
		this.options = Object.assign(
			{
				mode: "iso",          // 'iso' | 'orbit' | 'eye'
				scale: 1,
				showGrid: true,
				showWalls: true,
				showLabels: false,
				textures: true,
				assetRoot: "/assets/ioi_retail_suite/",
				materialTiles: null,  // {material: cm}, from api.material_catalog
				textureRoute: "/assets/ioi_retail_suite/textures/",
				textureTileCm: 50,
				tintOpacity: 0.16,   // family colour over the texture — above ~0.3
				                     // the wood stops looking like wood
				editable: false,     // drag an object on the floor in iso mode
				onSelect: null,      // (box) => void
				onMove: null,        // (id, {dx, dy, dz}) => void, live during a drag
				onMoveEnd: null,     // (id) => void
				onContext: null,     // (box, {x, y}) => void — right click
				eyeLabels: true,     // name tags over the objects, in the walkthrough
				eyeLabelMax: 26,     // beyond that the room reads as a wall of tags
				shadows: true,       // contact shadow under each object
				shellMaterials: null, // {floor, wall, ceiling}; guessed from the scene
				eyeTextureBudget: 160, // textured faces per frame, nearest first
				eyeTextureRange: 2600, // cm past which a tile is under a pixel anyway
				// In the walkthrough the point is what the room looks like, not
				// which family a piece belongs to : 16 % of saturated purple over
				// white laminate gives a lavender bed. The isometric view keeps
				// the stronger tint, because there it is the colour code that
				// makes the plan readable.
				eyeTintOpacity: 0.07,
				// a textured face wider than this is cut up : the tangent mapping
				// is first-order, so its error grows with the size of the face
				eyeFaceTileCm: 120,
				// The GPU draws the orbit and the walkthrough. The isometric view
				// stays vector : it is the one the PDF is drawn from, server-side,
				// and a test pins the two together. Set false to force the SVG
				// fallback everywhere — it is what runs anyway when WebGL is off.
				webgl: true,
			},
			options
		);
		this.scene = { grid: 10, room: { w: 400, d: 300, h: 250 }, boxes: [] };
		this.camera = { angle: 0, zoom: 1, tiltDeg: 58, spinDeg: -35 };
		// first-person camera : centimetres, degrees
		this.eye = { x: null, y: null, height: 175, yaw: 90, pitch: 0, fov: 72 };
		this.selected = null;
		this._unit = 1;
		this._dragged = false;
		this._bindPointer();
	}

	// ------------------------------------------------------------ public API
	setScene(scene) {
		this.scene = Object.assign({ grid: 10, room: { w: 400, d: 300, h: 250 }, boxes: [] }, scene);
		if (this.eye.x === null) this.resetEye();
		if (this._gl) this._gl._sceneStamp = null;
		this.render();
		return this;
	}

	setMode(mode) {
		this.options.mode = mode;
		if (mode === "eye" && this.eye.x === null) this.resetEye();
		this.render();
		return this;
	}

	/** Textures off gives flat colours — what the PDF export uses. */
	setTextures(on) {
		this.options.textures = !!on;
		if (this._gl && this._gl.ready) {
			this._gl.options.tintStrength = on ? this.options.eyeTintOpacity + 0.05 : 1;
			this._gl._materials.forEach((material, key) => {
				material.map = on ? material.userData.map || material.map : null;
				if (!material.userData.map) material.userData.map = material.map;
				material.needsUpdate = true;
			});
			this._gl.request();
		}
		this.render();
		return this;
	}

	/** Name tags over the objects, in the walkthrough. */
	setEyeLabels(on) {
		this.options.eyeLabels = !!on;
		if (this.options.mode === "eye") this.render();
		return this;
	}

	/** Height of the observer, in centimetres. */
	setEyeHeight(cm) {
		this.eye.height = Math.max(30, Math.min(280, Number(cm) || 175));
		if (this._gl) this._gl.setEyeHeight(this.eye.height);
		if (this.options.mode === "eye") this.render();
		return this;
	}

	/**
	 * Put the observer where the room can actually be seen.
	 *
	 * Standing in the middle of the furniture is useless : at one metre from a
	 * bed a 41° vertical field shows nothing but mattress, and the geometry
	 * runs through the near plane. So the observer walks in instead — from the
	 * wall with the most room in front of it, facing the furniture, tilted down
	 * just enough to frame it.
	 */
	resetEye() {
		const room = this.scene.room;
		const solids = (this.scene.boxes || []).filter((b) => b.z < this.eye.height && b.h > 15);

		if (!solids.length) {
			this.eye.x = room.w / 2;
			this.eye.y = Math.min(60, room.d / 6);
			this.eye.yaw = 90;
			this.eye.pitch = -6;
			return this;
		}

		// what the observer should be looking at
		let tx = 0;
		let ty = 0;
		let tz = 0;
		for (const b of solids) {
			tx += b.x + b.w / 2;
			ty += b.y + b.d / 2;
			tz += b.z + b.h / 2;
		}
		tx /= solids.length;
		ty /= solids.length;
		tz /= solids.length;

		// stand against a wall or in a corner, never in the middle of the room
		const inset = 45;
		const candidates = [
			[room.w / 2, inset], [room.w / 2, room.d - inset],
			[inset, room.d / 2], [room.w - inset, room.d / 2],
			[inset, inset], [room.w - inset, inset],
			[inset, room.d - inset], [room.w - inset, room.d - inset],
		];

		let best = null;
		for (const [x, y] of candidates) {
			let clear = Infinity;
			for (const b of solids) clear = Math.min(clear, clearanceTo(x, y, b));
			if (clear < 35) continue;
			// far enough to see the whole scene, close enough to read it
			const span = Math.hypot(tx - x, ty - y);
			const score = Math.min(span, 900) + clear * 0.5;
			if (!best || score > best.score) best = { x: x, y: y, score: score, span: span };
		}
		if (!best) best = { x: inset, y: inset, span: Math.hypot(tx - inset, ty - inset) };

		this.eye.x = best.x;
		this.eye.y = best.y;
		this.eye.yaw = (Math.atan2(ty - best.y, tx - best.x) / RAD + 360) % 360;
		if (this._gl) this._gl.setEye({ x: this.eye.x, y: this.eye.y, yaw: this.eye.yaw });
		// look down onto the furniture rather than over its head
		const drop = this.eye.height - tz;
		this.eye.pitch = Math.max(-35, Math.min(5, -Math.atan2(drop, best.span || 1) / RAD));
		return this;
	}

	/**
	 * Fill the screen with the viewer, and give it the keyboard.
	 *
	 * A walkthrough in a 600 px letterbox shows a 41° vertical slice ; full
	 * screen roughly doubles it, which is the difference between guessing at a
	 * room and standing in it.
	 */
	toggleFullscreen() {
		const doc = this.container.ownerDocument;
		if (doc.fullscreenElement === this.container) {
			if (doc.exitFullscreen) doc.exitFullscreen();
			return this;
		}
		const request =
			this.container.requestFullscreen ||
			this.container.webkitRequestFullscreen ||
			this.container.msRequestFullscreen;
		if (!request) {
			if (typeof frappe !== "undefined" && frappe.show_alert) {
				frappe.show_alert({
					message: "Le plein écran n'est pas autorisé par ce navigateur.",
					indicator: "orange",
				});
			}
			return this;
		}
		Promise.resolve(request.call(this.container)).then(
			() => this.container.focus(),
			() => {}
		);
		return this;
	}

	get isFullscreen() {
		const doc = this.container && this.container.ownerDocument;
		return !!doc && doc.fullscreenElement === this.container;
	}

	/**
	 * A DOM node to keep floating above the scene.
	 *
	 * Every renderer rebuilds `container.innerHTML` wholesale, so anything the
	 * caller parks inside would be wiped on the next frame. Registering it here
	 * makes the viewer put it back after each draw — which is how the object
	 * list and the inspector survive full screen.
	 */
	setOverlay(node) {
		this._overlay = node || null;
		if (this._overlay) {
			this.render();
			this._mountOverlay();
		}
		return this;
	}

	_mountOverlay() {
		// always on the container, never inside a layer that gets rebuilt
		if (this._overlay && this.container.appendChild) {
			this.container.appendChild(this._overlay);
		}
	}

	/** Turn on the spot until a world point is centred. */
	lookAt(point) {
		if (this._gl && this._gl.ready) {
			this._gl.lookAt(point);
			return this;
		}
		const dx = point[0] - this.eye.x;
		const dy = point[1] - this.eye.y;
		const dz = point[2] - this.eye.height;
		const flat = Math.sqrt(dx * dx + dy * dy);
		if (flat < 1) return this;
		this.eye.yaw = (Math.atan2(dy, dx) / RAD + 360) % 360;
		this.eye.pitch = Math.max(-80, Math.min(80, Math.atan2(dz, flat) / RAD));
		if (this.options.mode === "eye") this.render();
		return this;
	}

	/**
	 * Walk over to a piece and face it.
	 *
	 * Approaches from wherever the observer already stands, then tries other
	 * bearings and distances until it finds somewhere a body fits — in a busy
	 * room the obvious spot is usually occupied by another wardrobe.
	 */
	walkTo(point, distance = 220) {
		if (this._gl && this._gl.ready) {
			this.eye.x = this._gl.eye.x;
			this.eye.y = this._gl.eye.y;
		}
		const away = Math.atan2(this.eye.y - point[1], this.eye.x - point[0]);
		for (const radius of [distance, distance * 1.4, distance * 0.7, distance * 2]) {
			for (let step = 0; step < 12; step++) {
				// alternate either side of the current bearing, widening
				const swing = (step % 2 ? 1 : -1) * Math.ceil(step / 2) * 30 * RAD;
				const angle = away + swing;
				const x = point[0] + Math.cos(angle) * radius;
				const y = point[1] + Math.sin(angle) * radius;
				if (!this._standable(x, y)) continue;
				this.eye.x = x;
				this.eye.y = y;
				return this.lookAt(point);
			}
		}
		return this.lookAt(point); // nowhere to stand : at least turn towards it
	}

	/** Move the observer explicitly, in centimetres and degrees. */
	setEye(position) {
		if (position.x !== undefined) this.eye.x = Number(position.x);
		if (position.y !== undefined) this.eye.y = Number(position.y);
		if (position.yaw !== undefined) this.eye.yaw = Number(position.yaw);
		if (position.pitch !== undefined) this.eye.pitch = Number(position.pitch);
		if (this.options.mode === "eye") this.render();
		return this;
	}

	rotate(step = 1) {
		if (this._gl && this._gl.ready) {
			this._gl.rotate(step);
			return this;
		}
		if (this.options.mode === "eye") {
			this.eye.yaw = (this.eye.yaw + step * 45) % 360;
		} else {
			this.camera.angle = (this.camera.angle + step + 4) % 4;
		}
		this.render();
		return this;
	}

	zoom(factor) {
		if (this._gl && this._gl.ready) {
			this._gl.zoom(factor);
			return this;
		}
		if (this.options.mode === "eye") {
			this.eye.fov = Math.min(110, Math.max(35, this.eye.fov / factor));
		} else {
			this.camera.zoom = Math.min(4, Math.max(0.25, this.camera.zoom * factor));
		}
		this.render();
		return this;
	}

	/** Is there room for a body at this spot ? */
	_standable(x, y) {
		const room = this.scene.room;
		if (x < 5 || y < 5 || x > room.w - 5 || y > room.d - 5) return false;
		for (const b of this.scene.boxes || []) {
			if (b.z > this.eye.height || b.z + b.h < 20) continue; // steppable
			if (clearanceTo(x, y, b) < 18) return false;
		}
		return true;
	}

	/**
	 * Walk : forward is +, strafe is positive to the right.
	 *
	 * A blocked step slides along the obstacle instead of stopping dead, and a
	 * step that would end inside a solid is refused — walking through a
	 * wardrobe shows the inside of a crate and nothing else.
	 */
	walk(forward = 0, strafe = 0) {
		if (this._gl && this._gl.ready && this.options.mode === "eye") {
			this._gl.walk(forward, strafe);
			return this;
		}
		const yaw = this.eye.yaw * RAD;
		const dx = Math.cos(yaw) * forward + Math.sin(yaw) * strafe;
		const dy = Math.sin(yaw) * forward - Math.cos(yaw) * strafe;
		const x = this.eye.x;
		const y = this.eye.y;

		if (this._standable(x + dx, y + dy)) {
			this.eye.x = x + dx;
			this.eye.y = y + dy;
		} else if (this._standable(x + dx, y)) {
			this.eye.x = x + dx;
		} else if (this._standable(x, y + dy)) {
			this.eye.y = y + dy;
		}
		if (this.options.mode === "eye") this.render();
		return this;
	}

	select(id) {
		this.selected = id;
		if (this._gl && this._gl.ready) {
			this._gl.selected = id;
			this._gl._applySelection();
			this._gl.request();
		}
		this.render();
		if (this.options.onSelect) this.options.onSelect(this.boxById(id));
		return this;
	}

	boxById(id) {
		return (
			(this.scene.objects || []).find((b) => b.id === id) ||
			(this.scene.boxes || []).find((b) => b.id === id) ||
			null
		);
	}

	/** Is this part of the currently selected object ? */
	_isActive(box) {
		if (!this.selected) return false;
		return this.selected === box.id || this.selected === box.parent;
	}

	/**
	 * The container holds two layers, not one lump of markup.
	 *
	 * The vector renderers rebuild their layer wholesale on every frame ; the
	 * GPU one owns a canvas that must survive. Writing both into
	 * `container.innerHTML` would have one destroy the other.
	 */
	_layer(name) {
		// outside a browser — the test harness — there is nothing to layer
		if (typeof document === "undefined") return this.container;
		const key = "_layer_" + name;
		if (this[key] && this[key].parentNode === this.container) return this[key];
		const node = document.createElement("div");
		node.className = "r3d-layer r3d-layer-" + name;
		this.container.appendChild(node);
		this[key] = node;
		return node;
	}

	_showLayer(name) {
		if (typeof document === "undefined") return this.container;
		for (const other of ["flat", "gl"]) {
			const node = this["_layer_" + other];
			if (node) node.style.display = other === name ? "" : "none";
		}
		return this._layer(name);
	}

	render() {
		if (!this.container) return;
		this.container.classList.add("r3d-host");
		const wants3d = this.options.mode === "orbit" || this.options.mode === "eye";
		if (wants3d && this.options.webgl && !this._glFailed) {
			this._renderGL();
			return;
		}
		this._showLayer("flat");
		if (this.options.mode === "orbit") this._renderOrbit();
		else if (this.options.mode === "eye") this._renderEye();
		else this._renderIso();
	}

	// ------------------------------------------------------------------ WebGL
	/**
	 * Hand the orbit and the walkthrough to the GPU.
	 *
	 * Everything the caller already relies on is forwarded : selection, drag to
	 * move, right click for the material picker, the eye height, the labels.
	 * If WebGL cannot start — blocked, software rasteriser, ancient device —
	 * the flag flips once and the vector renderers take over for good.
	 */
	_renderGL() {
		const layer = this._showLayer("gl");
		if (this._gl && this._gl.ready) {
			this._syncGL();
			return;
		}
		if (this._glPending) return;
		this._glPending = true;
		layer.innerHTML = `<div class="r3d-booting">Chargement du moteur 3D…</div>`;

		const Renderer = typeof window !== "undefined" ? window.RetailGL : null;
		if (!Renderer) {
			this._glFailed = true;
			this._glPending = false;
			this.render();
			return;
		}
		const gl = new Renderer(layer, {
			assetRoot: this.options.assetRoot,
			textureTileCm: this.options.textureTileCm,
			materialTiles: this.options.materialTiles,
			shadows: this.options.shadows,
			onSelect: (box) => {
				this.selected = box ? (box.parent || box.id) : null;
				if (this.options.onSelect) this.options.onSelect(box);
			},
			onMove: this.options.onMove,
			onMoveEnd: this.options.onMoveEnd,
			onContext: this.options.onContext,
			onFrame: () => this._paintGLChrome(),
		});
		gl.mount().then(
			() => {
				layer.querySelectorAll(".r3d-booting").forEach((node) => node.remove());
				this._gl = gl;
				this._glPending = false;
				gl.frame();
				this._syncGL();
			},
			(error) => {
				console.warn("WebGL indisponible, rendu vectoriel", error);
				this._glFailed = true;
				this._glPending = false;
				this.render();
			}
		);
	}

	_syncGL() {
		const gl = this._gl;
		if (!gl || !gl.ready) return;
		if (gl._sceneStamp !== this.scene) {
			gl.setScene(this.scene);
			gl._sceneStamp = this.scene;
		}
		gl.setMode(this.options.mode);
		gl.eye.height = this.eye.height;
		gl.eye.x = this.eye.x === null ? gl.eye.x : this.eye.x;
		gl.eye.y = this.eye.y === null ? gl.eye.y : this.eye.y;
		gl.eye.yaw = this.eye.yaw;
		gl.eye.pitch = this.eye.pitch;
		gl.selected = this.selected;
		gl._applySelection();
		gl.request();
	}

	/** The HUD and the name tags, as DOM over the canvas. */
	_paintGLChrome() {
		const gl = this._gl;
		if (!gl) return;
		const layer = this._layer("gl");
		let chrome = layer.querySelector(".r3d-chrome");
		if (!chrome) {
			chrome = document.createElement("div");
			chrome.className = "r3d-chrome";
			layer.appendChild(chrome);
		}
		// the camera lives in the GPU renderer now, so read it back for the HUD
		this.eye.x = gl.eye.x;
		this.eye.y = gl.eye.y;
		this.eye.yaw = gl.eye.yaw;
		this.eye.pitch = gl.eye.pitch;
		this.eye.fov = gl.eye.fov;

		const tags = this.options.mode === "eye" ? this._glTags(gl) : "";
		chrome.innerHTML = this._legend()
			+ (this.options.mode === "eye" ? this._eyeHud() : "")
			+ tags;
	}

	_glTags(gl) {
		if (!this.options.eyeLabels) return "";
		const source = (this.scene.objects && this.scene.objects.length)
			? this.scene.objects
			: (this.scene.boxes || []).filter((b) => !b.parent);
		const kept = [];
		for (const object of source) {
			const top = [
				object.x + object.w / 2,
				object.y + object.d / 2,
				object.z + object.h,
			];
			const point = gl.project(top);
			if (!point.visible) continue;
			const rect = gl.canvas.getBoundingClientRect();
			if (point.x < 6 || point.x > rect.width - 6 || point.y < 18 || point.y > rect.height - 6) {
				continue;
			}
			const depth = Math.hypot(top[0] - gl.eye.x, top[1] - gl.eye.y);
			kept.push({ point, object, depth });
		}
		kept.sort((a, b) => a.depth - b.depth);
		const shown = [];
		for (const candidate of kept) {
			if (shown.length >= this.options.eyeLabelMax) break;
			const clash = shown.some(
				(other) => Math.abs(other.point.x - candidate.point.x) < 96
					&& Math.abs(other.point.y - candidate.point.y) < 24
			);
			if (!clash) shown.push(candidate);
		}
		return shown
			.map(({ point, object }) => {
				const slot = object.location
					? String(object.location).split("\u2022").pop().trim()
					: "";
				const what = object.item || object.label || "";
				const head = slot || String(object.label || what).slice(0, 26);
				const foot = slot
					? `${String(what).slice(0, 20)}${object.qty ? "  \u00d7" + Math.round(object.qty) : ""}`
					: "";
				const active = this.selected === (object.parent || object.id);
				return `<div class="r3d-domtag${active ? " is-active" : ""}"
					style="left:${point.x.toFixed(0)}px;top:${point.y.toFixed(0)}px">
					<b>${frappe.utils.escape_html(head)}</b>
					${foot ? `<span>${frappe.utils.escape_html(foot)}</span>` : ""}
				</div>`;
			})
			.join("");
	}

	// ------------------------------------------------------------- geometry
	/** Camera rotation applied before projection : 4 quarter turns. */
	_orient(x, y) {
		const room = this.scene.room;
		switch (this.camera.angle) {
			case 1: return [y, room.w - x];
			case 2: return [room.w - x, room.d - y];
			case 3: return [room.d - y, x];
			default: return [x, y];
		}
	}

	/** Inverse of _orient, applied to a delta rather than a point. */
	_unorientDelta(dox, doy) {
		switch (this.camera.angle) {
			case 1: return [-doy, dox];
			case 2: return [-dox, -doy];
			case 3: return [doy, -dox];
			default: return [dox, doy];
		}
	}

	_roomExtent() {
		const room = this.scene.room;
		return this.camera.angle % 2 === 0 ? [room.w, room.d] : [room.d, room.w];
	}

	/** 2:1 isometric projection, z upwards. */
	_project(x, y, z, unit) {
		const o = this._orient(x, y);
		return [(o[0] - o[1]) * ISO_X * unit, ((o[0] + o[1]) * ISO_Y - z) * unit];
	}

	static shade(hex, amount) {
		const clean = String(hex || "#8c9299").replace("#", "");
		const full = clean.length === 3 ? clean.split("").map((c) => c + c).join("") : clean;
		const num = parseInt(full, 16);
		const channel = (shift) => {
			const value = (num >> shift) & 0xff;
			const mixed = amount >= 0
				? value + (255 - value) * amount
				: value * (1 + amount);
			return Math.max(0, Math.min(255, Math.round(mixed)));
		};
		return `rgb(${channel(16)},${channel(8)},${channel(0)})`;
	}

	// ------------------------------------------------------------ SVG (iso)
	_renderIso() {
		const room = this.scene.room;
		const boxes = this.scene.boxes;
		const grid = this.scene.grid;
		const extent = this._roomExtent();
		const ew = extent[0];
		const ed = extent[1];
		// a host with a runaway height would flatten the room to a sliver
		const hostW = Math.min(this.container.clientWidth || 900, 2400);
		const hostH = Math.min(this.container.clientHeight || 560, 1400);

		// fit the whole room in the viewport, then apply the user zoom
		const spanX = (ew + ed) * ISO_X;
		const spanY = (ew + ed) * ISO_Y + room.h;
		const unit = Math.min(hostW / (spanX || 1), hostH / (spanY || 1)) * 0.86 * this.camera.zoom;

		const parts = [];
		const P = (x, y, z) => this._project(x, y, z, unit);

		// bounding box of the projection, to centre it
		const corners = [
			P(0, 0, 0), P(ew, 0, 0), P(0, ed, 0), P(ew, ed, 0),
			P(0, 0, room.h), P(ew, 0, room.h), P(0, ed, room.h), P(ew, ed, room.h),
		];
		const minX = Math.min.apply(null, corners.map((c) => c[0]));
		const maxX = Math.max.apply(null, corners.map((c) => c[0]));
		const minY = Math.min.apply(null, corners.map((c) => c[1]));
		const maxY = Math.max.apply(null, corners.map((c) => c[1]));
		const dx = (hostW - (maxX - minX)) / 2 - minX;
		const dy = (hostH - (maxY - minY)) / 2 - minY;
		this._unit = unit;
		const pt = (x, y, z) => {
			const p = P(x, y, z);
			return `${(p[0] + dx).toFixed(1)},${(p[1] + dy).toFixed(1)}`;
		};

		// ---- floor
		parts.push(
			`<polygon class="r3d-floor" points="${pt(0, 0, 0)} ${pt(ew, 0, 0)} ${pt(ew, ed, 0)} ${pt(0, ed, 0)}"/>`
		);

		// ---- floor grid
		if (this.options.showGrid) {
			const step = grid * 5; // a line every 50 cm
			for (let x = step; x < ew; x += step) {
				const a = pt(x, 0, 0).split(",");
				const b = pt(x, ed, 0).split(",");
				parts.push(`<line class="r3d-grid" x1="${a[0]}" y1="${a[1]}" x2="${b[0]}" y2="${b[1]}"/>`);
			}
			for (let y = step; y < ed; y += step) {
				const a = pt(0, y, 0).split(",");
				const b = pt(ew, y, 0).split(",");
				parts.push(`<line class="r3d-grid" x1="${a[0]}" y1="${a[1]}" x2="${b[0]}" y2="${b[1]}"/>`);
			}
		}

		// ---- two back walls
		if (this.options.showWalls) {
			parts.push(
				`<polygon class="r3d-wall r3d-wall-a" points="${pt(0, 0, 0)} ${pt(ew, 0, 0)} ${pt(ew, 0, room.h)} ${pt(0, 0, room.h)}"/>`,
				`<polygon class="r3d-wall r3d-wall-b" points="${pt(0, 0, 0)} ${pt(0, ed, 0)} ${pt(0, ed, room.h)} ${pt(0, 0, room.h)}"/>`
			);
		}

		// patterns needed by this frame, filled in by _isoBox
		this._patterns = new Map();

		// ---- parts, far to near : on the real centre, since a turned part no
		// longer sits where its corner says
		const ordered = (boxes || [])
			.map((box) => {
				const c = centroid3(boxCorners(box));
				const o = this._orient(c[0], c[1]);
				return { box: box, key: o[0] + o[1], z: c[2] };
			})
			.sort((a, b) => a.key - b.key || a.z - b.z);

		for (const entry of ordered) {
			parts.push(this._isoBox(entry.box, dx, dy, unit));
		}

		this._layer("flat").innerHTML =
			`<svg class="r3d-svg" viewBox="0 0 ${hostW} ${hostH}" width="100%" height="100%">
				<defs>
					<filter id="r3dShadow" x="-20%" y="-20%" width="140%" height="140%">
						<feDropShadow dx="0" dy="3" stdDeviation="4" flood-opacity="0.18"/>
					</filter>
					${this._patternDefs()}
				</defs>
				${parts.join("")}
			</svg>` + this._legend();

		this._wireBoxes();
		this._mountOverlay();
	}

	/**
	 * One part, drawn as the faces that actually face the viewer.
	 *
	 * A convex solid never overlaps itself once the back faces are culled, so
	 * the visible ones can be emitted in any order.
	 */
	_isoBox(box, dx, dy, unit) {
		const active = this._isActive(box);
		const tone = box.tone || 0;
		const stroke = active ? "#0f1419" : RetailRoom3D.shade(box.color, tone - 0.5);
		const width = active ? 2 : 0.8;
		const material = this.options.textures ? (box.material || null) : null;

		const world = boxCorners(box);
		const screen = world.map((p) => {
			const s = this._project(p[0], p[1], p[2], unit);
			return [s[0] + dx, s[1] + dy];
		});

		const faces = [];
		for (let index = 0; index < FACES.length; index++) {
			const indices = FACES[index][0];
			const flat = indices.map((i) => screen[i]);
			if (shoelace(flat) <= 0) continue;
			const n = faceNormal(box, index);
			const shade = RetailRoom3D.shade(box.color, tone + faceTone(n));
			const points = flat.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
			if (!material) {
				faces.push(`<polygon fill="${shade}" stroke="${stroke}" stroke-width="${width}" points="${points}"/>`);
				continue;
			}
			// the texture follows the face's own axes, so it reads as grain and
			// not as a sticker — whatever angle the object is turned to
			const id = this._pattern(material, indices, world, screen);
			// three layers : the lit colour, the grain, then the family tint.
			// The first one matters — without it a texture that fails to load
			// leaves a 16 % wash and the piece turns into a ghost.
			faces.push(
				`<polygon fill="${shade}" stroke="none" points="${points}"/>` +
				`<polygon fill="url(#${id})" stroke="none" points="${points}"/>` +
				`<polygon fill="${shade}" fill-opacity="${this.options.tintOpacity}" ` +
				`stroke="${stroke}" stroke-width="${width + 0.4}" points="${points}"/>`
			);
		}

		// one label per piece of furniture, not one per part
		const labelled = !box.parent || String(box.id).endsWith("#0");
		let label = "";
		if (this.options.showLabels && labelled) {
			const c = centroid3(world);
			const l = this._project(c[0], c[1], c[2] + box.h / 2, unit);
			label = `<text class="r3d-label" x="${(l[0] + dx).toFixed(1)}" y="${(l[1] + dy).toFixed(1)}">${frappe.utils.escape_html(box.label || "")}</text>`;
		}

		return `<g data-box="${box.parent || box.id}" class="r3d-box${active ? " is-active" : ""}"
			filter="url(#r3dShadow)"><title>${frappe.utils.escape_html(box.label || box.item)} — ${box.w}×${box.d}×${box.h} cm</title>${faces.join("")}${label}</g>`;
	}

	/**
	 * Register the pattern for one material on one face and return its id.
	 *
	 * The matrix maps the 256 px tile onto the face's two screen edges, so one
	 * tile always covers `textureTileCm` centimetres of real surface. For an
	 * unrotated box this reduces to the fixed isometric shear it replaces.
	 */
	_pattern(material, indices, world, screen) {
		const o = indices[0];
		const a = indices[1];
		const b = indices[3];
		const len = (p, q) => Math.sqrt(
			(q[0] - p[0]) * (q[0] - p[0]) +
			(q[1] - p[1]) * (q[1] - p[1]) +
			(q[2] - p[2]) * (q[2] - p[2])
		);
		const l1 = len(world[o], world[a]) || 1;
		const l2 = len(world[o], world[b]) || 1;
		const e1 = [screen[a][0] - screen[o][0], screen[a][1] - screen[o][1]];
		const e2 = [screen[b][0] - screen[o][0], screen[b][1] - screen[o][1]];
		const T = this.options.textureTileCm;
		const m = [
			(e1[0] * T) / (l1 * 256),
			(e1[1] * T) / (l1 * 256),
			(e2[0] * T) / (l2 * 256),
			(e2[1] * T) / (l2 * 256),
		];
		// Quantised hard : in perspective every face has a slightly different
		// matrix, and keeping three decimals produced 3 000 patterns for one
		// warehouse — 2 MB of SVG per frame. Two decimals share them, and a
		// texture scaled 1 % differently is not something anyone can see.
		const id = `tex-${material}-` + m.map((v) => v.toFixed(2)).join("_").replace(/[.\-]/g, "x");
		if (this._patterns && !this._patterns.has(id)) {
			this._patterns.set(id, { material: material, m: m });
		}
		return id;
	}

	/**
	 * A pattern nailed to the surface rather than to the canvas.
	 *
	 * Two things were wrong with the first attempt. `patternTransform` with no
	 * translation anchors the tile at the origin of the SVG, so the grain slid
	 * across a wardrobe the moment the camera moved. And the matrix was built
	 * from the corner-to-corner secant of the projected face, which is only
	 * right for an affine projection — under perspective a 2 m face drifted
	 * 49 cm between viewpoints.
	 *
	 * So the matrix is the **tangent of the projection taken at the centre of
	 * the face**, not the secant across it : exact at the anchor, first-order
	 * correct around it. Same face, same four viewpoints, the drift falls to
	 * 3,5 cm — and to a few millimetres on anything furniture-sized.
	 *
	 * `anchorWorld` is the world point the texture is pinned to. Tiles of one
	 * wall share it, so the grain runs across them instead of restarting.
	 */
	_anchoredPattern(material, world, anchorWorld, project) {
		const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
		const unit = (a) => {
			const l = Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2]) || 1;
			return [a[0] / l, a[1] / l, a[2] / l];
		};
		const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

		const d1 = unit(sub(world[1], world[0]));
		const d2 = unit(sub(world[3], world[0]));
		const centre = [
			(world[0][0] + world[2][0]) / 2,
			(world[0][1] + world[2][1]) / 2,
			(world[0][2] + world[2][2]) / 2,
		];
		const step = 0.5; // half a centimetre is enough to read the slope
		const at = (base, direction, k) => [
			base[0] + direction[0] * k, base[1] + direction[1] * k, base[2] + direction[2] * k,
		];
		const pc = project(centre);
		const p1 = project(at(centre, d1, step));
		const p2 = project(at(centre, d2, step));
		if (!pc || !p1 || !p2) return null;

		const T = this.options.textureTileCm;
		const a = ((p1[0] - pc[0]) / step) * (T / 256);
		const b = ((p1[1] - pc[1]) / step) * (T / 256);
		const c = ((p2[0] - pc[0]) / step) * (T / 256);
		const d = ((p2[1] - pc[1]) / step) * (T / 256);

		// where the centre sits in texture space, measured from the anchor
		const offset = sub(centre, anchorWorld || world[0]);
		const u0 = (dot(offset, d1) / T) * 256;
		const v0 = (dot(offset, d2) / T) * 256;
		const m = [a, b, c, d, pc[0] - (a * u0 + c * v0), pc[1] - (b * u0 + d * v0)];
		if (!m.every((value) => Number.isFinite(value))) return null;

		const id = `tex-${material}-${this._patterns.size}`;
		this._patterns.set(id, { material: material, m: m });
		return id;
	}

	_patternDefs() {
		if (!this._patterns || !this._patterns.size) return "";
		const out = [];
		this._patterns.forEach((entry, id) => {
			const m = entry.m;
			const tx = (m[4] || 0).toFixed(2);
			const ty = (m[5] || 0).toFixed(2);
			out.push(
				`<pattern id="${id}" patternUnits="userSpaceOnUse" width="256" height="256"
					patternTransform="matrix(${m[0].toFixed(5)} ${m[1].toFixed(5)} ${m[2].toFixed(5)} ${m[3].toFixed(5)} ${tx} ${ty})">
					<image href="${this.options.textureRoute}${entry.material}.jpg" x="0" y="0" width="256" height="256"
						preserveAspectRatio="none"/>
				</pattern>`
			);
		});
		return out.join("");
	}

	_legend() {
		const families = {};
		for (const box of this.scene.boxes || []) {
			families[box.color] = families[box.color] || box.label;
		}
		const chips = Object.keys(families)
			.slice(0, 8)
			.map((color) => `<span class="r3d-chip"><i style="background:${color}"></i></span>`)
			.join("");
		const stats = this.scene.stats || {};
		const objects = (this.scene.objects || this.scene.boxes || []).length;
		const eye = this.options.mode === "eye"
			? `<span>&#128065; ${Math.round(this.eye.height)} cm · ${Math.round(this.eye.fov)}°</span>`
			: "";
		return `<div class="r3d-hud">
			<span>${objects} meuble${objects > 1 ? "s" : ""}</span>
			<span>${this.scene.room.w}×${this.scene.room.d}×${this.scene.room.h} cm</span>
			${stats.volume_m3 ? `<span>${stats.volume_m3} m³</span>` : ""}
			${eye}
			<span class="r3d-chips">${chips}</span>
		</div>`;
	}

	// ------------------------------------------------- SVG (eye, perspective)
	/**
	 * Floor, walls and ceiling of the shell.
	 *
	 * A showroom floor is light wood, a warehouse slab is concrete — the scene
	 * says which one it is, and the caller can override.
	 */
	_shellMaterials() {
		if (this.options.shellMaterials) return this.options.shellMaterials;
		return this.scene.warehouse
			? { floor: "concrete", wall: "paint", ceiling: "paint" }
			: { floor: "wood-light", wall: "paint", ceiling: "paint" };
	}

	/**
	 * A large surface, cut into tiles.
	 *
	 * One SVG pattern is an affine map, and a floor under perspective is not
	 * affine — it converges. Worse, the floor straddles the camera, so it was
	 * dropped from texturing altogether and stayed flat grey, which is the one
	 * surface a walkthrough cannot afford to lose. Cutting it into metre tiles
	 * makes each one small enough for an affine texture to hold, and each tile
	 * is clipped on its own.
	 */
	static tiles(origin, uVec, vVec, step = 110, maxSteps = 10) {
		const uLen = Math.sqrt(uVec[0] * uVec[0] + uVec[1] * uVec[1] + uVec[2] * uVec[2]);
		const vLen = Math.sqrt(vVec[0] * vVec[0] + vVec[1] * vVec[1] + vVec[2] * vVec[2]);
		const nu = Math.max(1, Math.min(maxSteps, Math.round(uLen / step)));
		const nv = Math.max(1, Math.min(maxSteps, Math.round(vLen / step)));
		const at = (i, j) => [
			origin[0] + (uVec[0] * i) / nu + (vVec[0] * j) / nv,
			origin[1] + (uVec[1] * i) / nu + (vVec[1] * j) / nv,
			origin[2] + (uVec[2] * i) / nu + (vVec[2] * j) / nv,
		];
		const out = [];
		for (let i = 0; i < nu; i++) {
			for (let j = 0; j < nv; j++) {
				out.push([at(i, j), at(i + 1, j), at(i + 1, j + 1), at(i, j + 1)]);
			}
		}
		return out;
	}

	/** Camera basis : forward, right, up. Right-handed, z upwards. */
	_eyeBasis() {
		const yaw = this.eye.yaw * RAD;
		const pitch = this.eye.pitch * RAD;
		const f = [Math.cos(pitch) * Math.cos(yaw), Math.cos(pitch) * Math.sin(yaw), Math.sin(pitch)];
		// right = f × worldUp, normalised
		let r = [f[1], -f[0], 0];
		const rl = Math.sqrt(r[0] * r[0] + r[1] * r[1]) || 1;
		r = [r[0] / rl, r[1] / rl, 0];
		const u = [
			r[1] * f[2] - r[2] * f[1],
			r[2] * f[0] - r[0] * f[2],
			r[0] * f[1] - r[1] * f[0],
		];
		return { f: f, r: r, u: u };
	}

	/** World point to camera space : [right, up, depth]. */
	_toCamera(point, basis) {
		const px = point[0] - this.eye.x;
		const py = point[1] - this.eye.y;
		const pz = point[2] - this.eye.height;
		return [
			px * basis.r[0] + py * basis.r[1] + pz * basis.r[2],
			px * basis.u[0] + py * basis.u[1] + pz * basis.u[2],
			px * basis.f[0] + py * basis.f[1] + pz * basis.f[2],
		];
	}

	/** Sutherland–Hodgman against the single near plane, in camera space. */
	static clipNear(poly) {
		const out = [];
		for (let i = 0; i < poly.length; i++) {
			const a = poly[i];
			const b = poly[(i + 1) % poly.length];
			const ain = a[2] >= NEAR_CM;
			const bin = b[2] >= NEAR_CM;
			if (ain) out.push(a);
			if (ain !== bin) {
				const t = (NEAR_CM - a[2]) / (b[2] - a[2]);
				out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, NEAR_CM]);
			}
		}
		return out;
	}

	_renderEye() {
		const room = this.scene.room;
		const hostW = Math.min(this.container.clientWidth || 900, 2400);
		const hostH = Math.min(this.container.clientHeight || 560, 1400);
		// fov is the horizontal one ; a letterbox viewport therefore sees less
		// vertically, which is exactly what a real lens does
		const focal = hostW / 2 / Math.tan((this.eye.fov * RAD) / 2);
		const basis = this._eyeBasis();
		// patterns needed by this frame, filled in by push()
		this._patterns = new Map();
		const shell = this._shellMaterials();
		const textured = this.options.textures;

		const flatten = (cam) =>
			cam.map((p) => [hostW / 2 + (p[0] / p[2]) * focal, hostH / 2 - (p[1] / p[2]) * focal]);

		// the room shell and its contents are painted in two separate passes.
		// From inside a convex box the six inner faces tile the view without
		// ever overlapping, so they need no sorting — and mixing them into the
		// depth sort is what put the floor on top of the furniture : a face
		// clipped at the near plane ends up with a tiny average depth.
		const shellQuads = [];
		const shadowQuads = [];
		const quads = [];
		const push = (bucket, kind, world, fill, stroke, strokeWidth, owner, material, opacity,
		              tint, anchorWorld) => {
			const cam = world.map((p) => this._toCamera(p, basis));
			const clipped = RetailRoom3D.clipNear(cam);
			if (clipped.length < 3) return;
			// depth read on the unclipped face : clipping drags the centroid
			// onto the near plane and scrambles the order
			let depth = 0;
			for (const p of cam) depth += p[2];
			// a face that straddles the camera projects to nonsense, and one far
			// enough away has a tile smaller than a pixel : neither is textured
			const mean = depth / cam.length;
			const eligible =
				!!material &&
				mean < this.options.eyeTextureRange &&
				cam.every((point) => point[2] >= NEAR_CM);
			bucket.push({
				kind: kind,
				flat: flatten(clipped),
				fill: fill,
				stroke: stroke,
				strokeWidth: strokeWidth,
				depth: mean,
				owner: owner,
				pattern: null,
				material: eligible ? material : null,
				world: eligible ? world : null,
				cam: eligible ? cam : null,
				opacity: opacity,
				tint: tint === undefined ? this.options.tintOpacity : tint,
				// tiles of one wall share an anchor, so the grain runs across
				// them instead of restarting at every tile edge
				anchorWorld: eligible ? (anchorWorld || world[0]) : null,
			});
		};

		// ---- the shell of the room : floor, ceiling, four walls
		const w = room.w;
		const d = room.d;
		const h = room.h;
		// origin, then the two edge vectors of the surface
		const planes = [
			[[0, 0, 0], [w, 0, 0], [0, d, 0], "#e3e8ee", shell.floor],
			[[0, 0, h], [w, 0, 0], [0, d, 0], "#fbfcfd", shell.ceiling],
			[[0, 0, 0], [w, 0, 0], [0, 0, h], "#f2f5f8", shell.wall],
			[[0, d, 0], [w, 0, 0], [0, 0, h], "#eef2f6", shell.wall],
			[[0, 0, 0], [0, d, 0], [0, 0, h], "#e9eef3", shell.wall],
			[[w, 0, 0], [0, d, 0], [0, 0, h], "#e5eaf0", shell.wall],
		];
		for (const plane of planes) {
			const material = textured ? plane[4] : null;
			// untextured, one quad is enough and much cheaper
			const quadsOfPlane = material
				? RetailRoom3D.tiles(plane[0], plane[1], plane[2])
				: [[
					plane[0],
					[plane[0][0] + plane[1][0], plane[0][1] + plane[1][1], plane[0][2] + plane[1][2]],
					[plane[0][0] + plane[1][0] + plane[2][0], plane[0][1] + plane[1][1] + plane[2][1],
					 plane[0][2] + plane[1][2] + plane[2][2]],
					[plane[0][0] + plane[2][0], plane[0][1] + plane[2][1], plane[0][2] + plane[2][2]],
				]];
			for (const quad of quadsOfPlane) {
				push(shellQuads, "shell", quad, plane[3], material ? "none" : "#cfd6de",
				     material ? 0 : 0.7, null, material, undefined, undefined, plane[0]);
			}
		}

		// ---- contact shadows : without them everything floats
		if (this.options.shadows) {
			const grounded = (this.scene.objects && this.scene.objects.length)
				? this.scene.objects
				: (this.scene.boxes || []).filter((b) => !b.parent);
			for (const object of grounded) {
				const corners = boxCorners(object);
				const xs = corners.map((c) => c[0]);
				const ys = corners.map((c) => c[1]);
				const pad = 4;
				const x0 = Math.min.apply(null, xs) - pad;
				const x1 = Math.max.apply(null, xs) + pad;
				const y0 = Math.min.apply(null, ys) - pad;
				const y1 = Math.max.apply(null, ys) + pad;
				// a shelf hung high casts a fainter, wider mark
				const lift = Math.max(0, Math.min.apply(null, corners.map((c) => c[2])));
				const opacity = Math.max(0.05, 0.22 - lift / 900);
				push(shadowQuads, "shadow",
				     [[x0, y0, 0.6], [x1, y0, 0.6], [x1, y1, 0.6], [x0, y1, 0.6]],
				     "#0f1419", "none", 0, null, null, opacity);
			}
		}

		// ---- furniture, every face that turns towards the observer
		for (const box of this.scene.boxes || []) {
			const world = boxCorners(box);
			const active = this._isActive(box);
			const tone = box.tone || 0;
			const stroke = active ? "#0f1419" : RetailRoom3D.shade(box.color, tone - 0.5);
			for (let index = 0; index < FACES.length; index++) {
				const poly = FACES[index][0].map((i) => world[i]);
				const n = faceNormal(box, index);
				const c = centroid3(poly);
				const toEye = [this.eye.x - c[0], this.eye.y - c[1], this.eye.height - c[2]];
				if (n[0] * toEye[0] + n[1] * toEye[1] + n[2] * toEye[2] <= 0) continue;
				const material = textured ? (box.material || null) : null;
				const shade = RetailRoom3D.shade(box.color, tone + faceTone(n));
				// a big panel is cut down before it is textured, and every piece
				// keeps the same anchor so the grain runs across the whole face
				const pieces = material
					? RetailRoom3D.tiles(
						poly[0],
						[poly[1][0] - poly[0][0], poly[1][1] - poly[0][1], poly[1][2] - poly[0][2]],
						[poly[3][0] - poly[0][0], poly[3][1] - poly[0][1], poly[3][2] - poly[0][2]],
						this.options.eyeFaceTileCm,
						4
					)
					: [poly];
				for (const piece of pieces) {
					push(
						quads,
						"box",
						piece,
						shade,
						stroke,
						active ? 1.6 : 0.5,
						box.parent || box.id,
						material,
						undefined,
						this.options.eyeTintOpacity,
						poly[0]
					);
				}
			}
		}

		// ---- name tags : which article sits where, and in which slot
		const tags = this._eyeTags(basis, focal, hostW, hostH);

		quads.sort((a, b) => b.depth - a.depth); // painter : far first
		const ordered = shellQuads.concat(shadowQuads, quads);

		// Texture what is close enough to be worth it, nearest first. Beyond the
		// budget the face keeps its flat shade — at that distance the tint and
		// the texture average out to the same pixel anyway.
		const wanted = ordered.filter((q) => q.material);
		wanted.sort((a, b) => a.depth - b.depth);
		const project = (point) => {
			const cam = this._toCamera(point, basis);
			return cam[2] < NEAR_CM ? null : flatten([cam])[0];
		};
		for (const q of wanted.slice(0, this.options.eyeTextureBudget)) {
			q.pattern = this._anchoredPattern(q.material, q.world, q.anchorWorld, project);
		}

		const parts = ordered.map((q) => {
			const points = q.flat.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
			const cls = `r3d-${q.kind}`;
			const tag = q.owner ? ` data-box="${q.owner}"` : "";
			if (q.kind === "shadow") {
				return `<polygon class="${cls}" fill="${q.fill}" fill-opacity="${q.opacity.toFixed(3)}" stroke="none" points="${points}"/>`;
			}
			if (!q.pattern) {
				return `<polygon class="${cls}"${tag} fill="${q.fill}" stroke="${q.stroke}" stroke-width="${q.strokeWidth}" points="${points}"/>`;
			}
			// the texture carries the detail, the shade carries the lighting, and
			// the base underneath keeps the face solid if the tile never arrives
			return (
				`<polygon class="${cls} is-base" fill="${q.fill}" stroke="none" points="${points}"/>` +
				`<polygon class="${cls} is-tex" fill="url(#${q.pattern})" stroke="none" points="${points}"/>` +
				`<polygon class="${cls}"${tag} fill="${q.fill}" fill-opacity="${q.tint}" ` +
				`stroke="${q.stroke}" stroke-width="${q.strokeWidth}" points="${points}"/>`
			);
		});

		this._layer("flat").innerHTML =
			`<svg class="r3d-svg r3d-eye" viewBox="0 0 ${hostW} ${hostH}" width="100%" height="100%">
				<defs>
					<linearGradient id="r3dHaze" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stop-color="#ffffff" stop-opacity="0.10"/>
						<stop offset="100%" stop-color="#0f1419" stop-opacity="0.06"/>
					</linearGradient>
					${this._patternDefs()}
				</defs>
				${parts.join("")}
				<rect width="${hostW}" height="${hostH}" fill="url(#r3dHaze)" pointer-events="none"/>
				${tags}
			</svg>` + this._legend() + this._eyeHud();

		this._wireBoxes();
		this._mountOverlay();
	}

	/**
	 * A tag above each object : what it is, and where it is stored.
	 *
	 * Only the nearest few, and never two that would sit on top of each other —
	 * a warehouse aisle holds hundreds of pallets, and a hundred overlapping
	 * labels tell you less than none.
	 */
	_eyeTags(basis, focal, hostW, hostH) {
		if (!this.options.eyeLabels) return "";
		const source = (this.scene.objects && this.scene.objects.length)
			? this.scene.objects
			: (this.scene.boxes || []).filter((b) => !b.parent);
		if (!source.length) return "";

		const candidates = [];
		for (const object of source) {
			const world = boxCorners(object);
			const top = centroid3(world);
			top[2] = Math.max.apply(null, world.map((c) => c[2]));
			const cam = this._toCamera(top, basis);
			if (cam[2] < NEAR_CM * 2) continue;
			const x = hostW / 2 + (cam[0] / cam[2]) * focal;
			const y = hostH / 2 - (cam[1] / cam[2]) * focal;
			if (x < 4 || x > hostW - 4 || y < 14 || y > hostH - 4) continue;
			candidates.push({ x: x, y: y, depth: cam[2], object: object });
		}
		candidates.sort((a, b) => a.depth - b.depth);

		const kept = [];
		for (const candidate of candidates) {
			if (kept.length >= this.options.eyeLabelMax) break;
			const clash = kept.some(
				(other) => Math.abs(other.x - candidate.x) < 96 && Math.abs(other.y - candidate.y) < 22
			);
			if (!clash) kept.push(candidate);
		}

		return kept
			.map((tag) => {
				const object = tag.object;
				// a warehouse slot names itself ; a piece of furniture is its label
				const slot = object.location
					? String(object.location).split("\u2022").pop().trim()
					: "";
				const what = object.item || object.label || "";
				const head = slot || String(object.label || what).slice(0, 26);
				const foot = slot
					? `${String(what).slice(0, 20)}${object.qty ? "  \u00d7" + Math.round(object.qty) : ""}`
					: "";
				const width = Math.max(58, Math.max(head.length, foot.length) * 5.9 + 16);
				const height = foot ? 30 : 19;
				const active = this._isActive(object);
				return `<g class="r3d-tag${active ? " is-active" : ""}" data-box="${object.parent || object.id}"
					transform="translate(${tag.x.toFixed(1)} ${tag.y.toFixed(1)})">
					<line class="r3d-tag-lead" x1="0" y1="0" x2="0" y2="${-(height + 6)}"/>
					<rect class="r3d-tag-box" x="${(-width / 2).toFixed(1)}" y="${-(height + 6 + height)}"
						width="${width.toFixed(1)}" height="${height}" rx="5"/>
					<text class="r3d-tag-a" x="0" y="${-(height + 6 + height) + 12}"
						text-anchor="middle">${frappe.utils.escape_html(head)}</text>
					${foot
						? `<text class="r3d-tag-b" x="0" y="${-(height + 6 + height) + 24}"
							text-anchor="middle">${frappe.utils.escape_html(foot)}</text>`
						: ""}
				</g>`;
			})
			.join("");
	}

	_eyeHud() {
		const heading = Math.round((((this.eye.yaw % 360) + 360) % 360));
		return `<div class="r3d-eyehud">
			<b>Vue à hauteur d'homme</b>
			<span>Glisser : regarder · molette : avancer · Z/S/Q/D : marcher
				· F : ${this.isFullscreen ? "quitter le" : ""} plein écran
				· L : étiquettes</span>
			<span>Position ${Math.round(this.eye.x)} / ${Math.round(this.eye.y)} cm — cap ${heading}°
				· inclinaison ${Math.round(this.eye.pitch)}°</span>
		</div>`;
	}

	// ---------------------------------------------------------- CSS (orbit)
	_renderOrbit() {
		const room = this.scene.room;
		const boxes = this.scene.boxes;
		const hostW = Math.min(this.container.clientWidth || 900, 2400);
		const hostH = Math.min(this.container.clientHeight || 560, 1400);
		const unit = (Math.min(hostW / (room.w + room.d), hostH / (room.d + room.h)) || 0.6)
			* this.camera.zoom;

		const faceStyles = (box) => {
			const w = box.w;
			const d = box.d;
			const h = box.h;
			const c = box.color;
			const t = box.tone || 0;
			return [
				["top", `width:${w}px;height:${d}px;transform:translateZ(${h}px);background:${RetailRoom3D.shade(c, t + 0.18)}`],
				["bottom", `width:${w}px;height:${d}px;transform:rotateX(180deg);background:${RetailRoom3D.shade(c, t - 0.5)}`],
				["front", `width:${w}px;height:${h}px;transform:rotateX(-90deg) translateZ(${-d}px) translateY(${-h}px);transform-origin:top;background:${RetailRoom3D.shade(c, t - 0.1)}`],
				["back", `width:${w}px;height:${h}px;transform:rotateX(-90deg) translateY(${-h}px);transform-origin:top;background:${RetailRoom3D.shade(c, t - 0.3)}`],
				["left", `width:${d}px;height:${h}px;transform:rotateY(90deg) rotateZ(90deg) translateY(${-h}px);transform-origin:top left;background:${RetailRoom3D.shade(c, t - 0.22)}`],
				["right", `width:${d}px;height:${h}px;transform:translateX(${w}px) rotateY(90deg) rotateZ(90deg) translateY(${-h}px);transform-origin:top left;background:${RetailRoom3D.shade(c, t - 0.38)}`],
			]
				.map((entry) => {
					const tex = this.options.textures && box.material
						? `background-image:url(${this.options.textureRoute}${box.material}.jpg);` +
						  `background-size:${Math.max(24, this.options.textureTileCm)}px;` +
						  `background-blend-mode:soft-light;`
						: "";
					return `<div class="r3d-face r3d-${entry[0]}" style="${entry[1]};${tex}"></div>`;
				})
				.join("");
		};

		const html = (boxes || [])
			.map((box) => {
				// the pivot is the centre of the object's base : the same one the
				// SVG renderers turn around, so all three views agree
				const rot = box.rot || [0, 0, 0];
				const pivot = box.pivot || [box.x + box.w / 2, box.y + box.d / 2, box.z];
				const spin = isIdentity(rot)
					? ""
					: ` translate3d(${(pivot[0] - box.x).toFixed(2)}px,${(pivot[1] - box.y).toFixed(2)}px,${(pivot[2] - box.z).toFixed(2)}px)` +
					  ` rotateZ(${rot[2]}deg) rotateY(${rot[1]}deg) rotateX(${rot[0]}deg)` +
					  ` translate3d(${(box.x - pivot[0]).toFixed(2)}px,${(box.y - pivot[1]).toFixed(2)}px,${(box.z - pivot[2]).toFixed(2)}px)`;
				return `<div class="r3d-solid${this._isActive(box) ? " is-active" : ""}"
					data-box="${box.parent || box.id}"
					style="transform:translate3d(${box.x}px,${box.y}px,${box.z}px)${spin}"
					title="${frappe.utils.escape_html(box.label || box.item)}">${faceStyles(box)}</div>`;
			})
			.join("");

		// the world carries the room dimensions : without them it collapses to a
		// 0x0 box and the whole scene hangs off the centre of the stage
		this._layer("flat").innerHTML = `
			<div class="r3d-stage" style="perspective:${Math.round(1400 * unit)}px">
				<div class="r3d-world" style="width:${room.w}px;height:${room.d}px;
					transform:scale(${unit}) rotateX(${this.camera.tiltDeg}deg) rotateZ(${this.camera.spinDeg}deg)">
					<div class="r3d-ground" style="width:${room.w}px;height:${room.d}px"></div>
					${html}
				</div>
			</div>` + this._legend();

		this._wireBoxes();
		this._mountOverlay();
	}

	// ------------------------------------------------------------ interaction
	_wireBoxes() {
		this._layer("flat").querySelectorAll("[data-box]").forEach((node) => {
			node.addEventListener("click", (event) => {
				event.stopPropagation();
				if (this._dragged) return; // a drag is not a click
				this.select(node.dataset.box);
			});
			node.addEventListener("contextmenu", (event) => {
				event.preventDefault();
				event.stopPropagation();
				this.selected = node.dataset.box;
				this.render();
				if (this.options.onContext) {
					this.options.onContext(this.boxById(node.dataset.box), {
						x: event.clientX,
						y: event.clientY,
					});
				}
			});
		});
	}

	/**
	 * Screen delta to world delta on the floor, for the isometric view.
	 *
	 * Inverting the 2:1 projection at constant z : sx = (ox-oy)·0.866·u and
	 * sy = (ox+oy)·0.5·u, so each world axis comes back from both screen axes.
	 */
	_screenToFloor(dsx, dsy) {
		const u = this._unit || 1;
		const a = dsx / (ISO_X * u);
		const b = dsy / (ISO_Y * u);
		return this._unorientDelta((a + b) / 2, (b - a) / 2);
	}

	_bindPointer() {
		if (!this.container) return;
		let mode = null; // 'orbit' | 'look' | 'move' | 'raise'
		let lastX = 0;
		let lastY = 0;
		let target = null;

		this.container.addEventListener("pointerdown", (event) => {
			this._dragged = false;
			lastX = event.clientX;
			lastY = event.clientY;
			const node = event.target.closest ? event.target.closest("[data-box]") : null;

			if (this.options.mode === "eye") mode = "look";
			else if (this.options.mode === "orbit") mode = "orbit";
			else if (this.options.editable && node && event.button === 0) {
				target = node.dataset.box;
				mode = event.shiftKey ? "raise" : "move";
				this.selected = target;
			} else return;

			this.container.setPointerCapture(event.pointerId);
		});

		this.container.addEventListener("pointermove", (event) => {
			if (!mode) return;
			const dx = event.clientX - lastX;
			const dy = event.clientY - lastY;
			if (Math.abs(dx) + Math.abs(dy) > 2) this._dragged = true;
			lastX = event.clientX;
			lastY = event.clientY;

			if (mode === "orbit") {
				this.camera.spinDeg += dx * 0.4;
				this.camera.tiltDeg = Math.min(89, Math.max(5, this.camera.tiltDeg - dy * 0.3));
				this._renderOrbit();
			} else if (mode === "look") {
				this.eye.yaw += dx * 0.22;
				this.eye.pitch = Math.min(75, Math.max(-75, this.eye.pitch - dy * 0.2));
				this._renderEye();
			} else if (mode === "move" && this.options.onMove) {
				const world = this._screenToFloor(dx, dy);
				this.options.onMove(target, { dx: world[0], dy: world[1], dz: 0 });
			} else if (mode === "raise" && this.options.onMove) {
				this.options.onMove(target, { dx: 0, dy: 0, dz: -dy / (this._unit || 1) });
			}
		});

		const stop = () => {
			if ((mode === "move" || mode === "raise") && this.options.onMoveEnd) {
				this.options.onMoveEnd(target);
			}
			mode = null;
			target = null;
			// let the click handler see the flag, then clear it
			setTimeout(() => { this._dragged = false; }, 0);
		};
		this.container.addEventListener("pointerup", stop);
		this.container.addEventListener("pointercancel", stop);

		this.container.addEventListener(
			"wheel",
			(event) => {
				event.preventDefault();
				if (this.options.mode === "eye") this.walk(event.deltaY > 0 ? -14 : 14, 0);
				else this.zoom(event.deltaY > 0 ? 0.92 : 1.08);
			},
			{ passive: false }
		);

		// the viewport changes size on the way in and out of full screen, and
		// the whole projection is fitted to it — so it has to be drawn again
		const doc = this.container.ownerDocument;
		if (doc && doc.addEventListener) {
			doc.addEventListener("fullscreenchange", () => {
				if (!this.container.isConnected) return;
				this.render();
				if (this.isFullscreen) this.container.focus();
			});
		}

		// walking needs focus, so the container can take it
		this.container.setAttribute("tabindex", "0");
		this.container.addEventListener("keydown", (event) => {
			const pressed = String(event.key).toLowerCase();
			if (pressed === "f") {
				this.toggleFullscreen();
				event.preventDefault();
				return;
			}
			if (pressed === "l") {
				this.setEyeLabels(!this.options.eyeLabels);
				event.preventDefault();
				return;
			}
			if (this.options.mode !== "eye") return;
			const step = event.shiftKey ? 40 : 14;
			const key = String(event.key).toLowerCase();
			if (key === "z" || key === "w" || event.key === "ArrowUp") this.walk(step, 0);
			else if (key === "s" || event.key === "ArrowDown") this.walk(-step, 0);
			else if (key === "q" || key === "a" || event.key === "ArrowLeft") this.walk(0, -step);
			else if (key === "d" || event.key === "ArrowRight") this.walk(0, step);
			else return;
			event.preventDefault();
		});
	}
}

if (typeof window !== "undefined") window.RetailRoom3D = RetailRoom3D;
if (typeof module !== "undefined") module.exports = RetailRoom3D;
